import {
  type ReactNode,
  type MouseEvent,
  type PointerEvent,
  useState,
  useCallback,
} from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/utils/cn';

type Props = { wallet: string };

const BASE =
  'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium transition-all duration-300';
const IDLE =
  'bg-white/[0.06] text-text-tertiary hover:bg-white/[0.10] hover:text-text-secondary';
const COPIED = 'scale-110 bg-success/15 text-success';

function useCopyToClipboard(value: string): {
  copied: boolean;
  trigger: () => void;
} {
  const [copied, setCopied] = useState(false);
  const trigger = useCallback(() => {
    void window.navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);
  return { copied, trigger };
}

const stopDrag = (e: PointerEvent<HTMLButtonElement>): void =>
  e.stopPropagation();

export function CopyWalletButton({ wallet }: Props): ReactNode {
  const { copied, trigger } = useCopyToClipboard(wallet);
  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      trigger();
    },
    [trigger]
  );
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={stopDrag}
      aria-label={copied ? 'Wallet copiada' : 'Copiar wallet'}
      className={cn(BASE, copied ? COPIED : IDLE)}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      <span>{copied ? 'Copiado' : 'Wallet'}</span>
    </button>
  );
}
